import useAnalyticsPage from '../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../web-analytics/Page';
import { FundingIcon } from '../../assets/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useFetchedFunding } from '../hooks/useFetchedFunding';
import { FundingData, FundingPartner, FundingPartnerSearchResult } from '../types';
import { FundingPartnerTile } from '../components/FundingPartnerTile';
import { FundingConsultationBanner } from '../components/FundingConsultationBanner';
import { FundingDisclaimer } from '../components/FundingDisclaimer';
import { useRequestFundingMutation } from '../hooks/useRequestFundingMutation';
import { ConfirmModal } from '../../commons/components/ConfirmModal';
import { useIsTabletOrLarger } from '../../responsive/hooks/hooks';
import { useFundingConsultationLink } from '../hooks/useFundingConsultationLink';
import { openUrlInNewTab } from '../../util/window-utils';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { FundingSearch } from '../components/FundingSearch';
import { fundingPartnerHasMismatches, searchFundingPartners } from '../utils';

export const Funding = () => {
  useAnalyticsPage(Page.FUNDING);
  const analytics = useAnalytics();
  const {
    query: { data: funding },
    queryStatusComponent: fundingStatusComponent,
  } = useFetchedFunding();

  const [matchingSearchResults, setMatchingSearchResults] = useState<
    FundingPartnerSearchResult[] | null
  >(null);
  const [nonMatchingSearchResults, setNonMatchingSearchResults] = useState<
    FundingPartnerSearchResult[] | null
  >(null);
  const [searchResultsKey, setSearchResultsKey] = useState(new Date().valueOf());

  const [introductionConfirmationDialogKey, setIntroductionConfirmationDialogKey] = useState<
    number | null
  >();
  const { mutate: requestIntroduction } = useRequestFundingMutation(() =>
    setIntroductionConfirmationDialogKey(new Date().valueOf())
  );
  const isTabletOrLarger = useIsTabletOrLarger();
  const fundingConsultationLink = useFundingConsultationLink(funding);

  const onIntroductionRequested = (fundingPartner: FundingPartner, hasMismatches: boolean) => {
    if (!hasMismatches) {
      requestIntroduction({
        partnerName: fundingPartner.name,
        partnerCode: fundingPartner.code,
        funding: funding ?? {
          personalCreditScore: null,
          businessStartMonth: null,
          businessStartYear: null,
          sumOfLoanCustomerNeeds: null,
        },
      });
    } else if (fundingConsultationLink) {
      openUrlInNewTab(fundingConsultationLink, {
        analytics,
        analyticsEvent: AnalyticsEvent.CONSULTATION_SCHEDULER_OPENED,
        properties: { source: 'funding-partner-tile' },
      });
    }
  };

  const onFindCallback = useCallback((funding: FundingData) => {
    const results = searchFundingPartners(funding);
    setMatchingSearchResults(results.filter((x) => !fundingPartnerHasMismatches(x.match)));
    setNonMatchingSearchResults(results.filter((x) => fundingPartnerHasMismatches(x.match)));
    setSearchResultsKey(new Date().valueOf());
  }, []);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  useEffect(() => {
    if (funding && isFirstLoad) {
      setIsFirstLoad(false);
    } else {
      return;
    }
    if (
      funding.businessStartYear != null ||
      funding.businessStartMonth != null ||
      funding.personalCreditScore != null ||
      funding.sumOfLoanCustomerNeeds != null
    ) {
      onFindCallback(funding);
    }
  }, [isFirstLoad, funding, onFindCallback]);

  return (
    <div className="funding__container">
      <div>
        <FundingIcon className="align-text-bottom" />
        <span className="growegy-font-em growegy-font20--weight500">&nbsp;Funding</span>
      </div>
      {funding ? (
        <FundingSearch funding={funding} onFind={onFindCallback} />
      ) : (
        fundingStatusComponent
      )}
      {matchingSearchResults && nonMatchingSearchResults ? (
        <>
          {isTabletOrLarger && <FundingConsultationBanner funding={funding} />}
          <div key={searchResultsKey} className="funding__partners-container">
            <div className="funding__search-result-title">{`Current Funding Options (${matchingSearchResults.length}):`}</div>
            <>
              {matchingSearchResults.map((x) => (
                <FundingPartnerTile
                  key={x.fundingPartner.code}
                  fundingPartnerSearchResult={x}
                  onIntroductionRequested={() => onIntroductionRequested(x.fundingPartner, false)}
                />
              ))}
            </>
            <div className="funding__search-result-title">{`Potential Funding Options (${nonMatchingSearchResults.length}):`}</div>
            <>
              {nonMatchingSearchResults.map((x) => (
                <FundingPartnerTile
                  key={x.fundingPartner.code}
                  fundingPartnerSearchResult={x}
                  onIntroductionRequested={() => onIntroductionRequested(x.fundingPartner, true)}
                />
              ))}
            </>
            {!isTabletOrLarger && <FundingConsultationBanner funding={funding} />}
            <FundingDisclaimer />
          </div>
        </>
      ) : (
        <div className="flex-grow-1">
          <FundingConsultationBanner funding={funding} />
        </div>
      )}
      {!matchingSearchResults && !nonMatchingSearchResults && (
        <>
          <FundingDisclaimer />
          <div />
        </>
      )}
      {introductionConfirmationDialogKey && (
        <ConfirmModal
          key={introductionConfirmationDialogKey}
          title="Your request has been received"
          footer={{ type: 'show', applyButton: { label: 'OK', onClick: () => {} } }}
        >
          Our funding expert will review your request and will connect you with the lender shortly.
        </ConfirmModal>
      )}
    </div>
  );
};
